import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useProductionMaterials } from "@/hooks/useProductionMaterials";
import { formatDimensions } from "@/lib/formatters";

interface OrderMaterialsTabProps {
  orderNumber: string;
}

export function OrderMaterialsTab({ orderNumber }: OrderMaterialsTabProps) {
  const { data: materials = [], isLoading } = useProductionMaterials(orderNumber);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (materials.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground text-center py-8">
            Ei materiaaleja lisätty tilaukselle
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Komponentti</TableHead>
              <TableHead>Materiaali</TableHead>
              <TableHead>Kategoria</TableHead>
              <TableHead className="text-center">Mitat (L×K×P mm)</TableHead>
              <TableHead className="text-center">Määrä</TableHead>
              <TableHead className="text-right">Lähde</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {materials.map((material, index) => (
              <TableRow key={`${material.source_id}-${index}`}>
                <TableCell className="font-medium">
                  {(material.product_model_name || material.source_model_name) && (
                    <span className="text-xs text-blue-600 block">
                      {material.product_model_name || material.source_model_name}
                    </span>
                  )}
                  {material.component_name || "-"}
                </TableCell>
                <TableCell>
                  <div>
                    <span>{material.material_name || "Tuntematon"}</span>
                    {material.material_sku && (
                      <span className="text-muted-foreground text-xs ml-2">
                        ({material.material_sku})
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {material.material_category || "-"}
                </TableCell>
                <TableCell className="text-center">
                  {formatDimensions(material.width, material.height, material.thickness)}
                </TableCell>
                <TableCell className="text-center">{material.quantity}</TableCell>
                <TableCell className="text-right">
                  <Badge variant={material.source === "configuration" ? "default" : "secondary"}>
                    {material.source === "configuration" ? "Konfiguraatio" : "Manuaalinen"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
